/**
 * Scene Utility Functions
 * Pure utility functions for Three.js scene operations
 */
import * as THREE from "three";
import { SCENE_CONFIG } from "../config/sceneConfig";

/**
 * Extracts floor number from a node name
 * @param {string} name - Node name in format "Floor_X_..."
 * @returns {string} The floor number
 */
export const getFloorNumFromName = (name) => {
  return name.split("_")[1];
};

/**
 * Gets the highest point from raycaster intersections
 * @param {Array} intersects - Array of intersection objects
 * @returns {THREE.Vector3|undefined} The highest point position
 */
export const getHighestPointFromIntersections = (intersects) => {
  if (intersects && intersects.length > 0) {
    const highestPoint = intersects.reduce((highest, current) => {
      return current.point.y > highest.point.y ? current : highest;
    });
    return highestPoint.point;
  }
};

/**
 * Filters intersections to get only mesh objects within distance
 * @param {Array} intersects - Array of intersection objects
 * @returns {Array} Filtered array of mesh intersections
 */
export const getAllMeshesFromIntersections = (intersects) => {
  if (intersects && intersects.length > 0) {
    const meshes = intersects.filter(
      (intersect) =>
        intersect.distance < SCENE_CONFIG.RAYCAST_MAX_DISTANCE &&
        intersect.object &&
        intersect.object instanceof THREE.Mesh
    );
    return meshes;
  }
  return [];
};

/**
 * Gets start and end positions from a line geometry
 * @param {THREE.Line} line - The line object
 * @returns {Object} Object with start and end Vector3 positions
 */
export const getLinePositions = (line) => {
  const positions = line.geometry.attributes.position.array;

  const start = new THREE.Vector3(positions[0], positions[1], positions[2]);
  const end = new THREE.Vector3(positions[3], positions[4], positions[5]);

  return { start, end };
};

/**
 * Calculates the polar angle of camera position
 * @param {THREE.Camera} camera - The camera object
 * @returns {number} The polar angle (phi)
 */
export const calculatePolarAngle = (camera) => {
  const position = camera.position;
  const spherical = new THREE.Spherical();
  spherical.setFromVector3(position);
  return spherical.phi;
};

/**
 * Gets direction vector rotated 90 degrees left or right
 * @param {THREE.Vector3} currentDirection - Current direction vector
 * @param {string} targetDirection - "left" or "right"
 * @returns {THREE.Vector3} Rotated direction vector
 */
export const getDirection = (currentDirection, targetDirection) => {
  const quaternion = new THREE.Quaternion();

  if (targetDirection === "left") {
    quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
    return currentDirection.clone().applyQuaternion(quaternion);
  } else {
    quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.PI / 2);
    return currentDirection.clone().applyQuaternion(quaternion);
  }
};

/**
 * Calculates next camera position based on movement direction
 * @param {string} direction - Movement direction (up, down, forward, backward, left, right)
 * @param {THREE.Vector3} currentPosition - Current position
 * @param {THREE.Vector3} currentDirection - Current direction vector
 * @param {number} speed - Movement speed
 * @returns {THREE.Vector3} New position
 */
export const getNextPosition = (
  direction,
  currentPosition,
  currentDirection,
  speed
) => {
  const newPosition = currentPosition.clone();

  switch (direction) {
    case "up":
      newPosition.y += speed;
      break;
    case "down":
      newPosition.y -= speed;
      break;
    case "forward":
      newPosition.x += currentDirection.x * speed;
      newPosition.z += currentDirection.z * speed;
      break;
    case "backward":
      newPosition.x -= currentDirection.x * speed;
      newPosition.z -= currentDirection.z * speed;
      break;
    case "left":
      const leftDirection = getDirection(currentDirection, "left");
      newPosition.x += leftDirection.x * speed;
      newPosition.z += leftDirection.z * speed;
      break;
    case "right":
      const rightDirection = getDirection(currentDirection, "right");
      newPosition.x += rightDirection.x * speed;
      newPosition.z += rightDirection.z * speed;
      break;
    default:
      break;
  }

  return newPosition;
};

/**
 * Updates a line geometry with new start and end points
 * @param {THREE.Line} line - The line to update
 * @param {THREE.Vector3} start - Start position
 * @param {THREE.Vector3} end - End position
 */
export const updateLine = (line, start, end) => {
  line.geometry.setFromPoints([start, end]);
  line.geometry.verticesNeedUpdate = true;
};

/**
 * Updates a label/sprite position
 * @param {THREE.Sprite} label - The label sprite
 * @param {Object} position - Position object with x, y, z
 */
export const updateLabelPosition = (label, position) => {
  label.position.set(position.x, position.y, position.z);
};

/**
 * Finds all nodes in scene with given name
 * @param {THREE.Scene} scene - The scene to search
 * @param {string} name - Name to search for
 * @returns {Array} Array of matching nodes
 */
export const getNodesByName = (scene, name) => {
  if (!scene) return [];
  const nodes = [];
  scene.traverse((child) => {
    if (child.name === name) {
      nodes.push(child);
    }
  });
  return nodes;
};

/**
 * Finds nearest object below camera using raycasting
 * @param {THREE.Camera} camera - The camera
 * @param {THREE.Scene} scene - The scene
 * @returns {Object|null} Intersection result or null
 */
export const getNearestObjectBelowCamera = (camera, scene) => {
  if (!camera || !scene) return null;

  const origin = camera.position.clone();
  const direction = new THREE.Vector3(0, -1, 0);

  const raycaster = new THREE.Raycaster(origin, direction);
  const objects = [];
  scene.traverse((child) => {
    if (child.isMesh) objects.push(child);
  });

  const intersects = raycaster.intersectObjects(objects, true);
  if (intersects.length > 0) {
    return intersects[0];
  }
  return null;
};


/**
 * Gets camera position and look-at position for focusing on a building by label name
 * @param {string} buildingName - The name of the building label
 * @param {THREE.Scene} scene - The scene to search for the label
 * @param {Object} modelsRef - Reference object containing building models
 * @param {Function} getModelSize - Function to get model dimensions
 * @returns {Object|null} Object with cameraPosition, lookAtPosition, and name, or null if not found
 */
export const getCameraPositionFromLabel = (buildingName, scene, modelsRef, getModelSize) => {
  if (!scene || !modelsRef) return null;

  // Find the label node
  // const nodes = [];
  // scene.traverse((child) => {
  //   if (child.name === buildingName) {
  //     nodes.push(child);
  //   }
  // });

  // if (nodes.length === 0) return null;
  
  const labelNode = getNodesByName(scene, buildingName)[0];
  const buildingId = labelNode.userData.id;
  const buildingModel = modelsRef[buildingId];
  
  if (!buildingModel) return null;

  // Calculate model size
  const modelSize = getModelSize(buildingModel);

  // Calculate look-at position (center of building)
  const lookAtPosition = buildingModel.position.clone();
  lookAtPosition.y += modelSize.y / 2;

  // Calculate camera position based on label position
  const labelPosition = labelNode.position;
  const cameraPosition = {
    x: labelPosition.x,
    y: labelPosition.y - 20,
    z: labelPosition.z + 80
  };

  return {
    name: buildingName,
    cameraPosition,
    lookAtPosition
  };
};

/**
 * Creates an array of camera positions from building labels
 * @param {Array} buildings - Array of building objects with name property
 * @param {THREE.Scene} scene - The scene to search for labels
 * @param {Object} modelsRef - Reference object containing building models
 * @param {Function} getModelSize - Function to get model dimensions
 * @returns {Array} Array of objects with {name, cameraPosition, lookAtPosition}
 */
export const createBuildingPositionsArray = (buildings, scene, modelsRef, getModelSize) => {
  if (!buildings || !Array.isArray(buildings) || buildings.length === 0) {
    return [];
  }

  // Sort buildings by name
  const sortedBuildings = [...buildings].sort((a, b) => 
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
  );

  // Map sorted buildings to camera positions
  const positions = sortedBuildings
    .map(building => {
      try {
        const position = getCameraPositionFromLabel(
          building.name,
          scene,
          modelsRef,
          getModelSize
        );
        return position;
      } catch (error) {
        console.warn(`Could not get position for building: ${building.name}`, error);
        return null;
      }
    })
    .filter(pos => pos !== null);

  return positions;
};
